import { useCallback, useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { tagesstandHolen, zugangLesen, zugangLoeschen, type Tagesstand } from '../raum/api'
import { Reisekarte } from '../raum/Reisekarte'
import { Maskottchen } from '../spiel/Maskottchen'
import { Sterne, Konfetti } from '../spiel/Deko'

/**
 * Die Raumseite: gemeinsame Reise oben, Tagesrätsel darunter.
 *
 * Bewusst ohne Rangliste und ohne Punktestand. Es gibt nichts zu gewinnen und
 * niemanden zu schlagen. Sichtbar ist nur, wer heute schon geübt hat, und was
 * die Gruppe zusammen erreicht hat. Ein Kind, das an einem Tag nicht kann,
 * soll hier nicht als Letztes auftauchen.
 */
export function Raum() {
  const [stand, setStand] = useState<Tagesstand | null>(null)
  const [fehler, setFehler] = useState<string | null>(null)
  const [laedt, setLaedt] = useState(true)
  const navigate = useNavigate()
  const zugang = zugangLesen()

  const laden = useCallback(async () => {
    if (!zugang) {
      navigate('/raum/beitreten')
      return
    }
    try {
      setStand(await tagesstandHolen())
      setFehler(null)
    } catch (e) {
      const grund = (e as { grund?: string }).grund
      if (grund === 'nicht-im-raum' || grund === 'unbekannter-raum') {
        zugangLoeschen()
        navigate('/raum/beitreten')
        return
      }
      setFehler('Der Raum ist gerade nicht erreichbar. Üben geht trotzdem.')
    } finally {
      setLaedt(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate])

  useEffect(() => {
    void laden()
    // Alle zwei Minuten nachsehen, ob jemand dazugekommen ist. Häufiger wäre
    // sinnlos: Die anderen Kinder üben über den Tag verteilt, nicht im Takt.
    const uhr = window.setInterval(() => void laden(), 120_000)
    return () => window.clearInterval(uhr)
  }, [laden])

  if (laedt) {
    return (
      <div className="buehne">
        <Sterne />
        <div className="mitte">
          <Maskottchen gross />
          <p className="unterzeile">Einen Moment …</p>
        </div>
      </div>
    )
  }

  if (!stand) {
    return (
      <div className="buehne">
        <Sterne />
        <div className="mitte">
          <Maskottchen stimmung="trost" gross />
          <h1 className="titel">Kein Raum</h1>
          <p className="unterzeile">{fehler ?? 'Du bist in keinem Raum.'}</p>
          <div className="zeile">
            <Link className="knopf" to="/raum/beitreten">
              Raum beitreten
            </Link>
            <Link className="knopf knopf--leise" to="/">
              Zurück
            </Link>
          </div>
        </div>
      </div>
    )
  }

  const r = stand.raetsel

  return (
    <div className="buehne buehne--scroll">
      <Sterne anzahl={25} />
      {r.vollAufgedeckt && r.grund === 'alle-da' && <Konfetti anzahl={30} />}

      <div className="raum">
        <div className="kopfzeile">
          <Link className="knopf knopf--leise" to="/">
            Zurück
          </Link>
          <span className="zaehler" style={{ textAlign: 'right' }}>
            {stand.raum.name}
          </span>
        </div>

        <Reisekarte rundenGesamt={stand.reise.rundenGesamt} />

        <section className="raetsel">
          <h2 className="raetsel__frage">{r.frage}</h2>

          <div className={r.vollAufgedeckt ? 'raetsel__pointe raetsel__pointe--offen' : 'raetsel__pointe'}>
            {r.pointe}
          </div>

          <p className="raetsel__hinweis">{hinweis(stand)}</p>
        </section>

        <section className="wer">
          <h3 className="wer__titel">
            Heute geübt: {r.geschafftHeute} von {r.mitgliederAnzahl}
          </h3>
          <div className="zeile">
            {stand.mitglieder.map((m) => (
              <span
                key={m.spitzname}
                className={
                  m.heuteGeschafft ? 'person person--geschafft' : 'person'
                }
              >
                {m.heuteGeschafft ? '✓ ' : ''}
                {m.spitzname}
                {m.ichSelbst ? ' (du)' : ''}
              </span>
            ))}
          </div>
        </section>

        {!stand.ichHabeGeschafft && (
          <div className="zeile" style={{ marginTop: '3vmin' }}>
            <Link className="knopf" to="/uebung">
              Jetzt üben
            </Link>
          </div>
        )}

        {fehler && <p className="fehler">{fehler}</p>}

        <p className="fuss">
          Raum-Code: <strong>{stand.raum.code}</strong> · zum Weitergeben an neue Kinder
        </p>
      </div>
    </div>
  )
}

function hinweis(stand: Tagesstand): string {
  const r = stand.raetsel
  if (r.vollAufgedeckt && r.grund === 'alle-da') {
    return 'Alle haben heute geübt! Deshalb gibt es die Auflösung sofort.'
  }
  if (r.vollAufgedeckt) {
    return 'Aufgelöst. Morgen gibt es eine neue Frage.'
  }
  if (!stand.ichHabeGeschafft) {
    return `Übe deine Runde, dann werden mehr Buchstaben sichtbar. Um ${r.aufloesungStunde} Uhr wird sowieso aufgelöst.`
  }
  const fehlen = r.mitgliederAnzahl - r.geschafftHeute
  if (fehlen === 1) {
    return `Du warst schon dran. Ein Kind fehlt noch, dann seht ihr die Antwort sofort.`
  }
  return `Du warst schon dran. Noch ${fehlen} Kinder, dann seht ihr die Antwort sofort. Sonst um ${r.aufloesungStunde} Uhr.`
}
